<template>
  <div v-if="canThisUserView">
    <PermPlacementsFilters
      :current-user="currentUser"
      :filters="currentFilters"
      @change="onFiltersChange"
    />

    <PermPlacementsActions :title="allOrFiltered" />

    <tol-table
      :table-data="permPlacements"
      :columns="columns"
      :show-spinner="isLoading"
      :is-row-clickable="true"
      @row-click="handleRowClick($event)"
    >
      <template v-slot:tableHeader>
        <th>
          <!-- checkbox -->
        </th>

        <th>{{ $t('permPlacement.listing.labels.invoice_number') }}</th>

        <th-sortable
          :direction="currentSortField === 'name' ? currentSortDirection : 'none'"
          field-name="name"
          @change="onSortOrderChange"
          >{{ $t('permPlacement.listing.labels.name') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'client_name' ? currentSortDirection : 'none'"
          field-name="client_name"
          @change="onSortOrderChange"
          >{{ $t('permPlacement.listing.labels.client_name') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'start_date' ? currentSortDirection : 'none'"
          field-name="start_date"
          @change="onSortOrderChange"
          >{{ $t('permPlacement.listing.labels.start_date') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'branch_name' ? currentSortDirection : 'none'"
          field-name="branch_name"
          @change="onSortOrderChange"
          >{{ $t('permPlacement.listing.labels.branch_name') }}</th-sortable
        >

        <th-sortable
          :direction="currentSortField === 'status' ? currentSortDirection : 'none'"
          field-name="status"
          @change="onSortOrderChange"
          >{{ $t('permPlacement.listing.labels.status') }}</th-sortable
        >
      </template>

      <template v-slot:check="{ value }">
        <td @click.stop>
          <Checkbox
            size="small"
            :checked="value.isSelected"
            :modified="value.referenceNumber in modifiedPerms"
            @change="onToggleSelectPermPlacment(value.reference_number)"
          />
        </td>
      </template>

      <template v-slot:reference_number="{ value }">
        <router-link :to="value.reference_number">{{ value.reference_number }}</router-link>
      </template>

      <template v-slot:invoice_number="{ value }">
        {{ value.invoice_number }}
      </template>

      <template v-slot:name="{ value }">
        {{ value.name }}
      </template>

      <template v-slot:client_name="{ value }">
        {{ value.client_name }}
      </template>

      <template v-slot:branch_name="{ value }">
        {{ value.branch_name }}
      </template>

      <template v-slot:start_date="{ value }">
        <font-awesome-icon icon="calendar-alt" />
        {{ value.start_date }}
      </template>

      <template v-slot:status="{ value }">
        <Status :status="value.status" object-type="assignment" />
      </template>
    </tol-table>

    <Pagination
      :records-per-page="permsPerPage"
      :offset="currentPage"
      :record-count="recordCount"
      @change="onChangePaginationParams($event)"
    />

    <div class="module-action-container">
      <div class="comment">
        <InputField
          type="text"
          class="form-input"
          field-icon="comments"
          @input="updateComment($event)"
        />
      </div>
      <div>
        <tol-button
          v-if="canThisUserDoAction('PROCESS')"
          button-type="minor"
          class="module-action process"
          icon-name="check"
          :tooltip="$t('permPlacement.actionButtons.processSelected')"
          @click="doPermPlacementAction('PROCESS')"
        >
        </tol-button>
        <tol-button
          v-if="canThisUserDoAction('REJECT')"
          button-type="minor"
          class="module-action reject"
          icon-name="times"
          :tooltip="$t('permPlacement.actionButtons.rejectSelected')"
          @click="doPermPlacementAction('REJECT')"
        >
        </tol-button>
      </div>
    </div>
  </div>
</template>

<script>
import PermPlacementsFilters from './components/PermPlacementsFilters';
import PermPlacementsActions from './components/PermPlacementsActions';
import TolTable from '../../../../components/TolTable';
import TolButton from '../../../../components/TolButton';
import ThSortable from '../../../../components/ThSortable';
import Pagination from '../../../../components/Pagination';
import Checkbox from '../../../../components/Checkbox';
import Status from '../../../../components/Status';
import InputField from '../../../../components/InputField';
import { getInitialState } from '@/app/features/perm-placements/features/perm-placements-listing/perm-placements-listing.store';
import {
  PermPlacementsListingGetters,
  PermPlacementsListingActions
} from './perm-placements-listing.keys';
import { AppActions } from '@/app/app.keys';
import { equals } from 'ramda';
export default {
  name: 'PermPlacementsListing',
  components: {
    PermPlacementsFilters,
    PermPlacementsActions,
    TolTable,
    TolButton,
    ThSortable,
    Pagination,
    Checkbox,
    Status,
    InputField
  },
  data() {
    return {
      isOnScreenDataModified: false,
      comment: ''
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters[PermPlacementsListingGetters.PermPlacementIsLoading];
    },
    allOrFiltered() {
      return this.areAnyFiltersSet()
        ? 'permPlacement.listing.actions.titleFiltered'
        : 'permPlacement.listing.actions.titleAll';
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    canThisUserView() {
      return this.currentUser ? this.currentUser.permissions['PERM_PLACEMENT']['VIEW'] : false;
    },
    currentFilters() {
      return this.$store.getters[PermPlacementsListingGetters.PermPlacementsFilters];
    },
    permPlacements() {
      const perms = this.$store.getters[PermPlacementsListingGetters.PermPlacements];
      return perms;
    },
    columns() {
      return ['invoice_number', 'name', 'client_name', 'start_date', 'branch_name', 'status'];
    },
    permsPerPage() {
      return this.$store.getters[PermPlacementsListingGetters.PermPlacementsPerPage];
    },
    currentPage() {
      return this.$store.getters[PermPlacementsListingGetters.PermPlacementsListingOffset];
    },
    recordCount() {
      return this.$store.getters[PermPlacementsListingGetters.PermPlacementsFilteredRecordCount];
    },
    currentSortField() {
      return this.$store.getters[PermPlacementsListingGetters.PermPlacementsSortBy];
    },
    currentSortDirection() {
      return this.$store.getters[PermPlacementsListingGetters.PermPlacementsSortDirection];
    },
    modifiedPerms() {
      return this.$store.getters[PermPlacementsListingGetters.ModifiedPermPlacements];
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('permPlacement.listing.title'));
    this.$store.dispatch(PermPlacementsListingActions.RetrievePermPlacements);
  },
  methods: {
    onFiltersChange(filters) {
      this.$store.dispatch(PermPlacementsListingActions.SetPermPlacementsFilters, filters);
    },
    onSortOrderChange(sortOrder) {
      this.$store.dispatch(PermPlacementsListingActions.SetPermPlacementsSortOrder, sortOrder);
    },
    onChangePaginationParams(evt) {
      this.$store.dispatch(PermPlacementsListingActions.SetPermPlacementsPaginationParameters, evt);
    },
    areAnyFiltersSet() {
      const initFilters = getInitialState().permPlacementsFilters;
      const currentFilters = this.$store.getters[
        PermPlacementsListingGetters.PermPlacementsFilters
      ];
      for (let prop in initFilters) {
        if (!equals(initFilters[prop], currentFilters[prop])) return true;
      }
      return false;
    },
    onToggleSelectPermPlacment(referenceNumber) {
      this.isOnScreenDataModified = true;
      this.$store.dispatch(PermPlacementsListingActions.SelectPermPlacement, referenceNumber);
    },
    canThisUserDoAction(action) {
      if (action) return true;
    },
    doPermPlacementAction(action) {
      this.$store.dispatch(PermPlacementsListingActions.UpdatePermPlacements, {
        action,
        comment: this.comment
      });
      this.isOnScreenDataModified = false;
    },
    updateComment(comment) {
      this.comment = comment;
    },
    handleRowClick(evt) {
      this.$router.push(`/perm-placements/${evt.reference_number}`);
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
@import '@/app/styles/action-buttons.scss';
</style>
